import { createAttachmentCountColumns } from './column-definition/attachment-count-columns'
import { FieldTypeEnum } from './custom-field.types'
import { ModuleColumnDefinition, ModuleTypeEnum } from './module'
import { ProjectTypeEnum } from './project'

/**
 * Standard width categories for consistent column sizing across the system
 *
 * These widths provide a standardized approach to column sizing that ensures:
 * - Consistent user experience across all tables
 * - Appropriate space for different content types
 * - Proper minimum widths that allow for resizing without being too narrow
 * - Logical progression from very narrow icon columns to very wide content columns
 *
 * Usage guidelines:
 * - VERY_NARROW: For icons, small numbers, boolean indicators (35-50px)
 * - NARROW: For short text, priorities, small dropdowns (80-120px)
 * - MEDIUM: For standard text fields, dates, status fields (120-160px)
 * - WIDE: For longer text, assignees, groups, descriptions (140-200px)
 * - VERY_WIDE: For names, main content fields (200-280px)
 */
const COLUMN_WIDTHS = {
  // Very narrow - icons, small numbers, boolean indicators
  VERY_NARROW: { width: '50px', minWidth: '35px' },
  // Narrow - short text, priorities, small dropdowns
  NARROW: { width: '120px', minWidth: '80px' },
  // Medium - standard text fields, dates, status fields
  MEDIUM: { width: '160px', minWidth: '120px' },
  // Wide - longer text fields, assignees, groups
  WIDE: { width: '200px', minWidth: '140px' },
  // Very wide - names, descriptions, main content
  VERY_WIDE: { width: '280px', minWidth: '200px' },
} as const

/**
 * Standard columns for tasks within a project context
 * These are optimized for project task management with different defaults than general task management
 */
export const PROJECT_TASK_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'name',
    name: 'name',
    label: 'Task',
    description: 'The name of the task',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0, // First column
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.VERY_WIDE,
  },
  {
    id: 'taskNumber',
    name: 'taskNumber',
    label: 'Task #',
    description: 'Task number',
    isVisible: false, // Hidden by default
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 5,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'statusTypeId',
    name: 'statusTypeId',
    label: 'Status',
    description: 'Current status type of the task',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 10, // Second column
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'assignees',
    name: 'assignees',
    label: 'Assignees',
    description: 'Users assigned to the task',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 20, // Third column
    fieldType: 'MULTI_USER' as const,
    ...COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'priority',
    name: 'priority',
    label: 'Priority',
    description: 'Task priority level',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 30,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'scheduledFor',
    name: 'scheduledFor',
    label: 'Scheduled Date',
    description: 'Date when the task is scheduled to be worked',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 35,
    fieldType: 'DATE' as const,
    ...COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'dueDate',
    name: 'dueDate',
    label: 'Due Date',
    description: 'Date when the task is due',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 40,
    fieldType: 'DATE' as const,
    ...COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'group',
    name: 'group',
    label: 'Group',
    description: 'The group this task belongs to',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 50,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'bucketId',
    name: 'bucketId',
    label: 'Bucket',
    description: 'The bucket this task belongs to',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 55,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'storyPoints',
    name: 'storyPoints',
    label: 'Points',
    description: 'Estimated story points for the task',
    isVisible: false, // Default false - will be enabled per project type
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 60,
    fieldType: 'NUMBER' as const,
    ...COLUMN_WIDTHS.VERY_NARROW,
  },
  {
    id: 'comments',
    name: 'comments',
    label: 'Comments',
    description: 'Number of comments',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 80,
    fieldType: 'STRING' as const,
    width: '80px',
    minWidth: '80px',
    icon: 'MessageCircle',
  },
  {
    id: 'tags',
    name: 'tags',
    label: 'Tags',
    description: 'Task tags',
    isVisible: false, // Hidden by default but available in table customization
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 90,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'deployment',
    name: 'deployment',
    label: 'Deployment',
    description: 'Deployments this task is included in',
    isVisible: false, // Hidden by default but available in table customization
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 92,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'dependencies',
    name: 'dependencies',
    label: 'Dependencies',
    description: 'Task dependencies',
    isVisible: false, // Default false - will be enabled per project type
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 95,
    fieldType: 'STRING' as const,
    width: '35px',
    minWidth: '35px',
    icon: 'GitBranch',
  },
  {
    id: 'recurring',
    name: 'recurring',
    label: 'Recurring',
    description: 'Recurring pattern and status',
    isVisible: false, // Less important in project context
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 100,
    fieldType: 'STRING' as const,
    ...COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'estimatedHours',
    name: 'estimatedHours',
    label: 'Est. Hours',
    description: 'Estimated hours to complete the task',
    isVisible: false, // Hidden by default but available
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 110,
    fieldType: 'NUMBER' as const,
    ...COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'description',
    name: 'description',
    label: 'Description',
    description: 'Task description',
    isVisible: false, // Hidden by default in table view
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 120,
    fieldType: 'RICH_TEXT' as const,
    ...COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'completedAt',
    name: 'completedAt',
    label: 'Completed',
    description: 'Date when the task was completed',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 130,
    fieldType: 'DATE' as const,
    ...COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'createdAt',
    name: 'createdAt',
    label: 'Created',
    description: 'Date when the task was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 140,
    fieldType: 'DATE' as const,
    ...COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'updatedAt',
    name: 'updatedAt',
    label: 'Updated',
    description: 'Date when the task was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 150,
    fieldType: 'DATE' as const,
    ...COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'isArchived',
    name: 'isArchived',
    label: 'Archived',
    description: 'Whether the task is archived',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 160,
    fieldType: 'BOOLEAN' as const,
    ...COLUMN_WIDTHS.VERY_NARROW,
  },
  ...createAttachmentCountColumns('task', 170),
  // Note: moduleType is not included since we're in a project context
]

/**
 * Project-type-specific column configurations
 * Each project type has its own default column visibility and ordering
 */
export const PROJECT_TYPE_COLUMN_CONFIGS: Record<
  ProjectTypeEnum,
  {
    columnVisibility: Record<string, boolean>
    columnOrders: Record<string, number>
    columnWidths: Record<string, number>
  }
> = {
  [ProjectTypeEnum.BASIC]: {
    columnVisibility: {
      name: true,
      statusTypeId: true,
      assignees: true,
      priority: true,
      scheduledFor: false,
      dueDate: true,
      group: true,
      bucketId: false,
      storyPoints: false, // Not useful for basic projects
      comments: true,
      tags: false,
      dependencies: false, // Not enabled by default
      taskNumber: false,
      recurring: false,
      estimatedHours: false,
      description: false,
      completedAt: false,
      createdAt: false,
      updatedAt: false,
      isArchived: false,
    },
    columnOrders: {
      name: 0,
      statusTypeId: 10,
      assignees: 20,
      priority: 30,
      scheduledFor: 35,
      dueDate: 40,
      group: 50,
      bucketId: 55,
      storyPoints: 60,
      comments: 30,
      tags: 90,
      dependencies: 95,
      taskNumber: 97,
      recurring: 100,
      estimatedHours: 110,
      description: 120,
      completedAt: 130,
      createdAt: 140,
      updatedAt: 150,
      isArchived: 160,
    },
    columnWidths: {
      name: 280, // VERY_WIDE
      statusTypeId: 160, // MEDIUM
      assignees: 200, // WIDE
      priority: 120, // NARROW
      scheduledFor: 160, // MEDIUM
      dueDate: 160, // MEDIUM
      group: 200, // WIDE
      bucketId: 200, // WIDE
      storyPoints: 50, // VERY_NARROW
      comments: 80, // Keep compact icon column readable
      tags: 200, // WIDE
      dependencies: 35, // Keep current special size
      taskNumber: 120, // NARROW
      recurring: 120, // NARROW
      estimatedHours: 120, // NARROW
      description: 200, // WIDE
      completedAt: 160, // MEDIUM
      createdAt: 160, // MEDIUM
      updatedAt: 160, // MEDIUM
      isArchived: 50, // VERY_NARROW
    },
  },

  [ProjectTypeEnum.KANBAN]: {
    columnVisibility: {
      name: true,
      statusTypeId: true,
      assignees: true,
      priority: true,
      scheduledFor: false,
      dueDate: true,
      group: true,
      bucketId: false,
      storyPoints: false, // Not typically used in pure Kanban
      comments: true,
      tags: false,
      dependencies: false, // Not emphasized in Kanban
      taskNumber: false,
      recurring: false,
      estimatedHours: false,
      description: false,
      completedAt: false,
      createdAt: false,
      updatedAt: false,
      isArchived: false,
    },
    columnOrders: {
      name: 0,
      statusTypeId: 10,
      assignees: 20,
      priority: 30,
      scheduledFor: 35,
      dueDate: 40,
      group: 50,
      bucketId: 55,
      storyPoints: 60,
      comments: 30,
      tags: 90,
      dependencies: 95,
      taskNumber: 97,
      recurring: 100,
      estimatedHours: 110,
      description: 120,
      completedAt: 130,
      createdAt: 140,
      updatedAt: 150,
      isArchived: 160,
    },
    columnWidths: {
      name: 280, // VERY_WIDE
      statusTypeId: 160, // MEDIUM
      assignees: 200, // WIDE
      priority: 120, // NARROW
      scheduledFor: 160, // MEDIUM
      dueDate: 160, // MEDIUM
      group: 200, // WIDE
      bucketId: 200, // WIDE
      storyPoints: 50, // VERY_NARROW
      comments: 80, // Keep compact icon column readable
      tags: 200, // WIDE
      dependencies: 35, // Keep current special size
      taskNumber: 120, // NARROW
      recurring: 120, // NARROW
      estimatedHours: 120, // NARROW
      description: 200, // WIDE
      completedAt: 160, // MEDIUM
      createdAt: 160, // MEDIUM
      updatedAt: 160, // MEDIUM
      isArchived: 50, // VERY_NARROW
    },
  },

  [ProjectTypeEnum.SCRUM]: {
    columnVisibility: {
      name: true,
      statusTypeId: true,
      assignees: true,
      priority: true,
      scheduledFor: false,
      dueDate: false, // Hidden by default in SCRUM, but available for manual enabling
      group: true,
      bucketId: false,
      storyPoints: true, // Essential for Scrum
      comments: true,
      tags: false,
      dependencies: false, // Available but not default
      taskNumber: false,
      recurring: false,
      estimatedHours: false,
      description: false,
      completedAt: false,
      createdAt: false,
      updatedAt: false,
      isArchived: false,
    },
    columnOrders: {
      name: 0,
      statusTypeId: 10,
      assignees: 20,
      priority: 30,
      scheduledFor: 35,
      dueDate: 40,
      group: 50,
      bucketId: 55,
      storyPoints: 60,
      comments: 30,
      tags: 90,
      dependencies: 95,
      taskNumber: 97,
      recurring: 100,
      estimatedHours: 110,
      description: 120,
      completedAt: 130,
      createdAt: 140,
      updatedAt: 150,
      isArchived: 160,
    },
    columnWidths: {
      name: 280, // VERY_WIDE
      statusTypeId: 160, // MEDIUM
      assignees: 200, // WIDE
      priority: 120, // NARROW
      scheduledFor: 160, // MEDIUM
      dueDate: 160, // MEDIUM
      group: 200, // WIDE
      bucketId: 200, // WIDE
      storyPoints: 50, // VERY_NARROW
      comments: 80, // Keep compact icon column readable
      tags: 200, // WIDE
      dependencies: 35, // Keep current special size
      taskNumber: 120, // NARROW
      recurring: 120, // NARROW
      estimatedHours: 120, // NARROW
      description: 200, // WIDE
      completedAt: 160, // MEDIUM
      createdAt: 160, // MEDIUM
      updatedAt: 160, // MEDIUM
      isArchived: 50, // VERY_NARROW
    },
  },

  [ProjectTypeEnum.WATERFALL]: {
    columnVisibility: {
      name: true,
      statusTypeId: true,
      assignees: true,
      priority: true,
      scheduledFor: false,
      dueDate: true,
      group: true,
      bucketId: false,
      storyPoints: false, // Not typically used in Waterfall
      comments: true,
      tags: false,
      dependencies: true, // Important for Waterfall
      taskNumber: false,
      recurring: false,
      estimatedHours: true, // Important for Waterfall planning
      description: false,
      completedAt: false,
      createdAt: false,
      updatedAt: false,
      isArchived: false,
    },
    columnOrders: {
      name: 0,
      statusTypeId: 10,
      assignees: 20,
      priority: 30,
      scheduledFor: 35,
      dueDate: 40,
      group: 50,
      bucketId: 55,
      dependencies: 60, // Higher priority in Waterfall
      estimatedHours: 70, // Higher priority in Waterfall
      comments: 90,
      tags: 95,
      storyPoints: 100,
      taskNumber: 105,
      recurring: 110,
      description: 120,
      completedAt: 130,
      createdAt: 140,
      updatedAt: 150,
      isArchived: 160,
    },
    columnWidths: {
      name: 280,
      statusTypeId: 140,
      assignees: 160,
      priority: 120,
      scheduledFor: 140,
      dueDate: 140,
      group: 140,
      bucketId: 180,
      dependencies: 30, // Compact width
      estimatedHours: 110, // Wider for Waterfall
      comments: 80,
      tags: 140,
      storyPoints: 80,
      taskNumber: 80,
      recurring: 80,
      description: 200,
      completedAt: 130,
      createdAt: 130,
      updatedAt: 130,
      isArchived: 80,
    },
  },
}

/**
 * Helper function to get project-type-specific column configuration
 */
export function getProjectTypeColumnConfig(projectType: ProjectTypeEnum): {
  columnVisibility: Record<string, boolean>
  columnOrders: Record<string, number>
  columnWidths: Record<string, number>
} {
  return (
    PROJECT_TYPE_COLUMN_CONFIGS[projectType] ||
    PROJECT_TYPE_COLUMN_CONFIGS[ProjectTypeEnum.BASIC]
  )
}

/**
 * Helper function to get the module type for project tasks
 */
export function getProjectTaskModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.TASK
}

/**
 * Helper function to combine project task columns with custom fields and apply visibility preferences
 */
export function getProjectTaskColumns(
  customFields: any[] = [],
  preferences?: Record<string, boolean> | any,
): ModuleColumnDefinition[] {
  // Extract column visibility preferences
  let columnVisibility: Record<string, boolean> = {}

  if (preferences) {
    if (preferences.columnPreferences) {
      // It's the full ColumnVisibilityPreference object
      columnVisibility = preferences.columnPreferences
    } else if (typeof preferences === 'object') {
      // It's a simple Record<string, boolean>
      columnVisibility = preferences as Record<string, boolean>
    }
  }

  // Extract column order preferences
  let columnOrders: Record<string, number> = {}

  if (preferences && preferences.columnOrders) {
    columnOrders = preferences.columnOrders
  }

  // Apply preferences to project task columns
  const standardColumns = PROJECT_TASK_STANDARD_COLUMNS.map(
    (column: ModuleColumnDefinition) => {
      // Check if there's a custom order for this column
      const orderKey = `${column.id}_order`
      const customOrder = columnOrders[orderKey]

      // Check if we have a visibility preference for this column
      const hasVisibilityPref = columnVisibility[column.id] !== undefined
      const visibilityValue = hasVisibilityPref
        ? !!columnVisibility[column.id]
        : column.isVisible

      return {
        ...column,
        // Override visibility if preferences exist
        isVisible: visibilityValue,
        // Apply custom order if available
        order: customOrder !== undefined ? customOrder : column.order,
      }
    },
  )

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const columnId = `custom_${field.fieldName}`
    const orderKey = `${columnId}_order`
    const customOrder = columnOrders[orderKey]

    // Check if we have a visibility preference for this column
    const hasVisibilityPref = columnVisibility[columnId] !== undefined
    const visibilityValue = hasVisibilityPref
      ? !!columnVisibility[columnId]
      : !!field.isActive

    // Parse dropdown options from settings if available
    const fieldOptions =
      (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT') &&
      field.settings?.options &&
      Array.isArray(field.settings.options)
        ? field.settings.options.map((opt: any) => {
            if (typeof opt === 'string') return { value: opt, label: opt }
            return {
              value: String(opt.value || opt.id || opt.label || ''),
              label: String(opt.label || opt.name || opt.value || ''),
            }
          })
        : undefined

    return {
      id: columnId,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible: visibilityValue,
      isRequired: false,
      isSystem: false,
      // Apply custom order if available, otherwise use default ordering (after tags at 90)
      order: customOrder !== undefined ? customOrder : 91 + index,
      fieldType: field.fieldType,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    }
  }) as ModuleColumnDefinition[]

  // Combine both types of columns
  return [...standardColumns, ...customColumns]
}

/**
 * Get default visible columns for a specific project type
 */
export function getDefaultVisibleColumnsForProjectType(
  projectType: ProjectTypeEnum,
): string[] {
  const config = getProjectTypeColumnConfig(projectType)
  return Object.entries(config.columnVisibility)
    .filter(([, isVisible]) => isVisible)
    .map(([columnId]) => columnId)
}

/**
 * Default visible columns for project tasks (optimized for project management)
 * This is now a fallback - use getDefaultVisibleColumnsForProjectType instead
 */
export const DEFAULT_PROJECT_TASK_VISIBLE_COLUMNS =
  getDefaultVisibleColumnsForProjectType(ProjectTypeEnum.BASIC)

/**
 * Compact view columns for project tasks (minimal set for overview)
 */
export const COMPACT_PROJECT_TASK_COLUMNS = [
  'name',
  'statusTypeId',
  'assignees',
  'dueDate',
]

/**
 * Detailed view columns for project tasks (comprehensive set for detailed management)
 */
export const DETAILED_PROJECT_TASK_COLUMNS = [
  'name',
  'statusTypeId',
  'assignees',
  'priority',
  'scheduledFor',
  'dueDate',
  'group',
  'bucketId',
  'storyPoints',
  'estimatedHours',
  'tags',
  'comments',
  'dependencies',
  'taskNumber',
  'recurring',
  'completedAt',
  'createdAt',
  'updatedAt',
]
